import { Box } from '@mui/material';
import React from 'react';
import { SubmittedRulesetFormValues } from 'modules/analyse/types/RulesetFormValues';
import { RulesetForm } from 'modules/analyse/components/RulesetForm';
import { AutomationTaskInput } from 'generated-graphql/types';
import { ToastType, useToast } from 'services/toast';
import {
  initializeCandidateBatchGroup,
  processCandidateBatches
} from 'services/candidate/api';
import { splitCandidateBatchesRequestIntoChucks } from 'services/candidate/utils/splitCandidateBatchesRequestIntoChucks';
import { AtsJobInput } from 'services/candidate/types';
import { Paths } from 'services/navigation/paths';
import { useNavigate } from 'react-router-dom';

type Props = {
  atsJobs: AtsJobInput[];
};

export const ATSCreateRulesetForMultipleJobsStep: React.FC<Props> = ({
  atsJobs
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const onFormSubmitted = async (values: SubmittedRulesetFormValues) => {
    const { analysisRequirements, groupName } = values;

    const automationTasks = [
      ...values.ruleAutomationTasks,
      ...values.actionAutomationTasks
    ].map<AutomationTaskInput>((item) => {
      return {
        action: item.action,
        requirementConditions: item.requirementConditions
      };
    });

    if (!groupName) {
      showToast({
        message: 'Ruleset name is required',
        type: ToastType.Error
      });
      return;
    }

    try {
      const { id } = await initializeCandidateBatchGroup({
        name: groupName,
        automationTasks,
        analysisRequirements
      });

      const atsJobChucks = splitCandidateBatchesRequestIntoChucks(atsJobs);

      await Promise.all(
        atsJobChucks.map((atsJobs) =>
          processCandidateBatches({
            atsJobs,
            candidateBatchGroupId: id
          })
        )
      );

      navigate(`${Paths.ANALYSE_RULESETS}/${id}`);

      showToast({
        message: `Your jobs have been added to ${groupName}`,
        type: ToastType.Success
      });
    } catch {
      showToast({
        title: 'Something went wrong',
        message: 'We could not create this ruleset. Please try again.',
        type: ToastType.Error
      });
    }
  };

  return (
    <Box>
      <RulesetForm
        showRulesetNameInput={true}
        onFormSubmitted={onFormSubmitted}
        saveAsRulesetInitialValue={false}
        isEditable
      />
    </Box>
  );
};
